import React, { useState, useCallback, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionDetails from '@mui/material/AccordionDetails';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';

import _get from 'lodash/get';
import _has from 'lodash/has';

import { ProfileContext } from '../../../data/auth-service';
import OSAnomalyService from '../../../data/os-anomaly-service';
import OSAnomalyDescriptions from '../../../data/os-anomaly-descriptions';

import SparklineGroup from '../../../components/charts/sparklinegroup';

import { RuleTitle } from '../../../libraries/helpers';


const useStyles = makeStyles(theme => ({
    container: {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
        marginTop: 100,
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
    },
    section: {
        marginBottom: theme.spacing(3),
        display: 'flex',
        padding: theme.spacing(2),
        paddingRight: 0,
    },
    chart: {
        marginTop: theme.spacing(2),
        width: '100%',
    },
    analysis: {
        marginTop: theme.spacing(2),
        fontSize: '0.9rem',
    },
}));

const sparklineSettings = {
    margin: 40,
    labelWidth: 0,
    chartHeight: 100,
    scale: 'linear',
    vgrid: true,
};

const AnomalyDetail = ({ rules, anom, onClose, width }) => {
    const theme = useTheme();
    const classes = useStyles();
    const { updateProfile, getProfile } = useContext(ProfileContext);
    const [anomaly, setAnomaly] = useState({});

    const [anomalyHistory, setAnomalyHistory] = useState();
    const [featureHistory, setFeatureHistory] = useState();

    const [expanded, setExpanded] = useState({
        anomaly: true,
        feature: true,
    });

    const onExpand = useCallback(v => {
        setExpanded(current => ({
            ...current,
            [v]: !current[v],
        }));
        const profile = getProfile('anomalydetail');
        const current = _get(profile, 'panels', {
            anomaly: true,
            feature: true,
        });
        updateProfile('anomalydetail', {
            ...profile,
            panels: { ...current, [v]: !current[v] },
        });
    }, [getProfile, updateProfile]);

    useEffect(() => {
        // read user pofile
        const profile = getProfile('anomalydetail');
        setExpanded(_get(profile, 'panels', true));
    }, [getProfile]);

    // fake anomaly history for now
    useEffect(() => {
        // Get single anomaly with additional details
        const aID = _get(anom, '_id', null);
        if (aID) {
            OSAnomalyService.getAnomaly(aID).then(res => {
                if(Object.prototype.toString.call(res).includes('Array') && res.length) {
                    setAnomaly(res[0]);
                }
            }, err => {
                console.error(err);
            });
        }

        // 15 minute intervals
        // const generateTimeline = (start, end, freq) => {
        //     const timeline = [];
        //     const interval = freq * 60 * 1000;
        //     for (let time = start; time <= end; time += interval) {
        //         timeline.push({
        //             timestamp: new Date(time).toISOString(),
        //             value: Math.random() * 100,
        //         });
        //     }
        //     return timeline;
        // };

        // const start = new Date().getTime() - 7 * 24 * 60 * 60 * 1000; // 7 days
        // const end = new Date().getTime();

        // setAnomalyHistory({
        //     start: new Date(start).toISOString(),
        //     end: new Date(end).toISOString(),
        //     metrics: [{
        //         index: 0,
        //         key: 'anomalies',
        //         metrics: [
        //             {
        //                 trendline: 99,
        //                 key: 'anomalies',
        //                 type: 'line',
        //                 minY: 0,
        //                 title: '',
        //                 value: d => _get(d, 'value', 0),
        //                 tipFormat: (v, d) => `Anomaly Occurrences: ${v}`,
        //             }],
        //         timeline: generateTimeline(start, end, 120),
        //     }],
        // });

        // setFeatureHistory({
        //     start: new Date(start).toISOString(),
        //     end: new Date(end).toISOString(),
        //     metrics: [{
        //         index: 0,
        //         key: 'feature',
        //         metrics: [
        //             {
        //                 // trendline: 99,
        //                 key: 'feature',
        //                 type: 'line',
        //                 minY: 0,
        //                 title: '',
        //                 value: d => _get(d, 'value', 0),
        //                 tipFormat: (v, d) => `Feature Data: ${v}`,
        //             }],
        //         timeline: generateTimeline(start, end, 15),
        //     }],
        // });
    }, [anom]);

    const getRuleField = (ruleID, field) => {
        const r = rules.find(x => x.uuid === ruleID);
        return _get(r, field, '');
    };

    const getSeverity = (grade) => {
        if ( grade < 0.4 ) {
            return 'Low';
        } else if ( grade >= 0.4 && grade < 0.6 ) {
            return 'Medium';
        } else if ( grade >= 0.6 && grade < 0.8 ) {
            return 'High';
        } else if ( grade >= 0.8 ) {
            return 'Critical';
        }
    };

    const getPopulatedDescription = (a) => {
        const ruleName = getRuleField(_get(a, '_source.detector_id'), 'name');
        if (_has(OSAnomalyDescriptions, ruleName)) {
            return OSAnomalyDescriptions[ruleName](a);
        }
    };

    return (
        <Box className={classes.container}>
            <Box className={classes.header}>
                <Typography variant="h5">Anomaly Details</Typography>
                <IconButton onClick={onClose}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
            <Grid container direction={'row'} className={classes.section}>
                <Grid item xs={12} >
                    <span>Anomaly ID:</span> {_get(anomaly, '_id', '')}
                </Grid>
                <Grid item xs={12} >
                    <span>Rule Name:</span> {RuleTitle(getRuleField(_get(anomaly, '_source.detector_id', ''), 'name'))}
                </Grid>
                <Grid item xs={12}>
                    <span>Confidence:</span> {_get(anomaly, '_source.confidence', 0).toFixed(2)}
                </Grid>
                <Grid item xs={12}>
                    <span>Severity:</span> {getSeverity(_get(anomaly, '_source.anomaly_grade', 0))}
                </Grid>
                <Grid item xs={12}>
                    <span>Creation Date:</span> {Moment(_get(anomaly, '_source.execution_end_time', 0)).format(theme.date.long)}
                </Grid>
            </Grid>
            <Box className={classes.section}>
                <Typography variant="body1"><strong>Description:</strong> {getRuleField(_get(anomaly, '_source.detector_id', ''), 'description')}</Typography>
            </Box>
            {/* <Grid container direction={'row'}>
                <Box className={classes.chart}>
                    <Accordion
                        TransitionProps={{ unmountOnExit: true }}
                        expanded={expanded.anomaly}
                        onChange={() => onExpand('anomaly')}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={'anomaly-content'}>
                            <AutoGraphIcon fontSize="small" />
                            <Typography className={classes.panelHeading}>Anomaly Ocurrances</Typography>
                        </AccordionSummary>
                        <AccordionDetails className={classes.panelDetails}>
                            {anomalyHistory && <SparklineGroup
                                metrics={anomalyHistory.metrics}
                                height={sparklineSettings.chartHeight + sparklineSettings.margin}
                                start={anomalyHistory.start}
                                end={anomalyHistory.end}
                                settings={sparklineSettings}
                                width={width - 55}>
                            </SparklineGroup>}
                        </AccordionDetails>
                    </Accordion>
                </Box>
            </Grid> */}
            {/* <Grid container direction={'row'}>
                <Box className={classes.chart}>
                    <Accordion
                        TransitionProps={{ unmountOnExit: true }}
                        expanded={expanded.feature}
                        onChange={() => onExpand('feature')}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={'feature-content'}>
                            <AutoGraphIcon fontSize="small" />
                            <Typography className={classes.panelHeading}>Feature Data</Typography>
                        </AccordionSummary>
                        <AccordionDetails className={classes.panelDetails}>
                            {featureHistory && <SparklineGroup
                                metrics={featureHistory.metrics}
                                height={sparklineSettings.chartHeight + sparklineSettings.margin}
                                start={featureHistory.start}
                                end={featureHistory.end}
                                settings={sparklineSettings}
                                width={width - 55}>
                            </SparklineGroup>}
                        </AccordionDetails>
                    </Accordion>
                </Box>
            </Grid> */}
            <Box p={2}>
                <Box>
                    <Typography variant="body1"><strong>Analysis (Dynamic per record):</strong></Typography>
                </Box>
                <Box>
                    <Typography className={classes.analysis}>
                        {/* {anomaly.analysis} */}
                        {getPopulatedDescription(anomaly)}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

AnomalyDetail.propTypes = {
    anom: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    width: PropTypes.number,
};

export default AnomalyDetail;
